import * as SwitchPrimitives from "@radix-ui/react-switch"
import * as React from "react"

import { cn } from "~/utils/helpers"

import { usePermissionGate, type PermissionGateProps } from "./PermissionTooltip"

/**
 * Binary on/off toggle. Two sizes (small 32x18, xsmall 26x16). Pass `indeterminate`
 * for the mixed-selection state (flat bar centered on the track). Pass `requires` to
 * permission-gate the toggle — disables itself and renders its own permission tooltip.
 * Canonical intent: {@link ../design-system/intents.ts SWITCH_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
    /**
     * `small` (32x18, default) or `xsmall` (26x16) per Figma. `medium` is legacy
     * (2 call sites remaining) and will be removed once migrated — do not use in new code.
     */
    size?: "small" | "xsmall" | "medium"
    /** Mixed-selection state (e.g. partially-checked parent). Renders a flat bar centered on the track. */
    indeterminate?: boolean
  } & PermissionGateProps
>(
  (
    {
      className,
      size = "small",
      indeterminate = false,
      disabled,
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    const { blocked, gate } = usePermissionGate({
      requires,
      permissionReason,
      allowedTitle,
      tooltipSide,
      wrapperClassName,
    })

    const toggle = (
      <SwitchPrimitives.Root
        ref={ref}
        disabled={disabled || blocked}
        className={cn(
          "group peer flex h-fit p-[2px] shrink-0 cursor-pointer items-center rounded-full shadow-switch-background transition",
          "focus-visible:outline-none focus-visible:shadow-borders-interactive-with-shadow!",
          "data-[state=unchecked]:bg-switch-off hover:data-[state=unchecked]:bg-switch-off-hover data-[state=unchecked]:data-[force-hover]:bg-switch-off-hover",
          "data-[state=checked]:bg-interactive hover:data-[state=checked]:bg-interactive-hover data-[state=checked]:data-[force-hover]:bg-interactive-hover",
          indeterminate &&
            "bg-interactive! hover:bg-interactive-hover! data-[force-hover]:bg-interactive-hover!",
          "disabled:cursor-not-allowed disabled:bg-disabled! hover:disabled:bg-disabled!",
          size === "medium" ? "w-11 h-6" : size === "small" ? "w-8 h-[18px]" : "w-[26px] h-[16px]",
          className,
        )}
        {...props}
      >
        {indeterminate ? (
          <div
            className={cn(
              "rounded-full bg-fg-on-color shadow-switch-handle group-disabled:bg-fg-disabled",
              size === "small"
                ? "h-1 w-3 mx-auto"
                : size === "xsmall"
                  ? "h-1 w-2.5 mx-auto"
                  : "h-1 w-4 mx-auto",
            )}
          />
        ) : (
          <SwitchPrimitives.Thumb
            className={cn(
              "pointer-events-none block rounded-full bg-fg-on-color shadow-switch-handle ring-0 transition-transform",
              "group-disabled:bg-fg-disabled",
              size === "medium"
                ? "h-5 w-5 data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
                : size === "small"
                  ? "h-[14px] w-[14px] data-[state=checked]:translate-x-3.5 data-[state=unchecked]:translate-x-0"
                  : "h-[12px] w-[12px] data-[state=checked]:translate-x-2.5 data-[state=unchecked]:translate-x-0",
            )}
          />
        )}
      </SwitchPrimitives.Root>
    )

    return gate(toggle)
  },
)
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
